import { useCallback, useMemo } from "react";
import format from "date-fns/format";

import Box from "@mui/material/Box";
import MuiButton from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { Button } from "@covertree/ui";
import { useResetNewPolicy } from "@hooks/newPolicy";
import { POLICIES_PATH } from "@routing/paths";

interface Props {
  decPageUrl: string;
}
export function CongratulationsView({ decPageUrl }: Props) {
  const {
    policyLocator,
    startDate,
    isAgentsSignResponsibility,
    physicallyMailDocuments,
    policyholder: {
      mailingAddress: { city, unitNumber, streetAddress, state, zipCode },
      emailAddress,
      firstName,
      middleName,
      lastName,
    },
  } = useNewBusinessSelector(selectPolicy);

  const resetNewPolicy = useResetNewPolicy();

  const formattedLotUnit = useMemo(
    () => unitNumber?.trim()?.replace(/^([^#])/, "#$1"),
    [unitNumber]
  );

  const formattedStartDate = useMemo(
    () => format(new Date(`${startDate} 00:00:00`), "MMMM do, yyyy"),
    [startDate]
  );

  const withoutESignRequest = useMemo(
    () => physicallyMailDocuments || isAgentsSignResponsibility,
    [physicallyMailDocuments, isAgentsSignResponsibility]
  );

  const policyholderName = useMemo(
    () =>
      middleName
        ? `${firstName} ${middleName} ${lastName}`
        : `${firstName} ${lastName}`,
    [firstName, middleName, lastName]
  );

  const handleViewPolicyDetails = useCallback(() => {
    resetNewPolicy(`/${POLICIES_PATH}/${policyLocator}`);
  }, [policyLocator, resetNewPolicy]);

  return (
    <Box>
      <img
        src="/animations/congrats.gif"
        data-testid="congrats-image"
        alt="congratulations!"
      />
      <Typography variant="h3" mb={3}>
        Congratulations, the policy has been issued!
      </Typography>
      <Stack
        spacing={2}
        direction="row"
        sx={(theme) => ({
          backgroundColor: "action.selected",
          borderRadius: theme.spacing(1.5),
          p: 2,
        })}>
        <Stack flex={1} direction="column">
          <Typography component="span" color="secondary">
            Effective {formattedStartDate}
          </Typography>
          <Typography variant="h4">Policy # {policyLocator}</Typography>
          {physicallyMailDocuments ? (
            <Typography variant="h6">
              Policy documents will {withoutESignRequest ? "" : "also"} be
              mailed to:
              <br />
              <Typography variant="h6" component="span" color="secondary">
                {streetAddress},{" "}
                {formattedLotUnit ? `${formattedLotUnit},` : ""}
                <br />
                {city}, {state} {zipCode}
              </Typography>
            </Typography>
          ) : (
            <Typography variant="h6">
              Policy documents were sent to {policyholderName}&#39;s
              <br /> email address:{" "}
              <Typography
                variant="h6"
                component="span"
                color="secondary"
                display="inline">
                {emailAddress}
              </Typography>
            </Typography>
          )}
        </Stack>
        <Stack direction="column" spacing={2} alignItems="flex-end">
          <Button onClick={handleViewPolicyDetails}>View policy details</Button>
          <MuiButton href={decPageUrl} target="_blank">
            Download PDF
          </MuiButton>
        </Stack>
      </Stack>
      <Box
        mt={2}
        sx={(theme) => ({
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: "action.focus",
          borderRadius: theme.spacing(1.5),
          p: 2,
        })}>
        {withoutESignRequest ? (
          <>
            <Typography variant="h4">Policy Application Signature</Typography>
            <Typography variant="subtitle1" color="text.disabled">
              Policy Application must be <strong>signed manually</strong>.
              Document can be uploaded in the Policy Details.
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h4">Policy Application eSignature</Typography>
            <Typography variant="subtitle1" my={2} color="text.disabled">
              Your customer will receive an email from{" "}
              <Typography
                color="text.disabled"
                display="inline"
                component="span"
                variant="subtitle1"
                fontWeight={500}>
                Dropbox Sign
              </Typography>{" "}
              regarding eSigning the policy application. This email will be sent
              to{" "}
              <Typography
                component="span"
                variant="subtitle1"
                color="secondary">
                {emailAddress}
              </Typography>
            </Typography>
            <Typography variant="subtitle2" fontWeight={500}>
              This step is needed in order to complete the purchase
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
